import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { ProgressModalComponent } from '@app/core/components/grid/modals/progress-modal/progress-modal.component';
import { ListButton } from '@app/core/components/list/list.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { FileItem, FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { TranslateService } from '@app/core/services/translate.service';
import { DocumentViewerGridDialogComponent } from '@app/modal/document-viewer-grid-dialog/document-viewer-grid-dialog.component';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { GraphMailService } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { DocArcService } from '@app/services/doc-arc.service';
import { DocumentItem, DocumentService } from '@app/services/document.service';
import { EmailCreateBeforeGet, EmailCreateClientSend, EmailCreateGet, EmailCreateParams, EmailCreatePictureBankItem, EmailCreateSendExternalAttachmentItem, EmailCreateSendParams, EmailCreateService, ReportItem } from '@app/services/email-create.service';
import { ReportService } from '@app/services/report.service';

type EmailCreateDocumentTabId = 'CUSTOMER' | 'ARGTYPE' | 'PRODUCT' | 'CUSTACT';
const ARGTYPE_DOCUMENTS_TAB_TRANSLATION = 'emailcreate_documents_argtype_tab';

interface EmailCreateDocumentSelections {
    customer: Map<string, '1' | '0'>;
    argtype: Map<string, '1' | '0'>;
    product: Map<string, '1' | '0'>;
    custact: Map<string, '1' | '0'>;
}

interface EmailCreateIncludedAttachment {
    kind: 'report' | 'document' | 'external';
    trackId: string | File;
    name: string;
    icon: string;
    iconColor?: string;
    sourceTranslation: string;
    sourceId?: string;
    file?: File;
    size?: number;
}

@GridModalComponent('emailcreate')
@Component({
    selector: 'tt-email-create',
    templateUrl: './email-create.component.html',
    styleUrls: ['./email-create.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailCreateComponent extends ComponentBaseComponent implements OnChanges {
    @Input()
    public params?: EmailCreateParams;

    public get?: EmailCreateGet;

    @Output()
    public getLoaded = new EventEmitter<EmailCreateGet>();

    @Output()
    public submit = new EventEmitter<EmailCreateGet>();

    public emailTemplates: Item[] = [];

    public reports: ReportItem[] = [];

    public customerDocuments: DocumentItem[] = [];

    public argtypeDocuments: DocumentItem[] = [];

    public productDocuments: DocumentItem[] = [];

    public custactDocuments: DocumentItem[] = [];

    public externalAttachments: File[] = [];

    public customerSirvImages: EmailCreatePictureBankItem[] = [];

    public argtypeSirvImages: EmailCreatePictureBankItem[] = [];

    public productSirvImages: EmailCreatePictureBankItem[] = [];

    public custactSirvImages: EmailCreatePictureBankItem[] = [];

    public selectedAttachmentTab = '';

    public selectedDocsTab = '';

    public selectedSirvTab = '';

    public argtypeDocumentsLabel = ARGTYPE_DOCUMENTS_TAB_TRANSLATION;

    public editorProcessing = false;

    public sending = false;

    public documentListButtons: ListButton<DocumentItem>[] = [
        {
            id: 'preview',
            icon: 'far fa-file-search',
            type: 'primary',
            tooltip: 'preview',
            onClick: (event) => this.dialog.open(DocumentViewerGridDialogComponent, { width: '80vw', data: { row: event.item } }),
        },
    ];

    public reportListButtons: ListButton<ReportItem>[] = [];

    private initialSelectedReport: number = -1;

    private translations = {
        '': '',
        error: '',
        retrieving_files_from_fileserver: '',
        saving_reports: '',
        sending_email: '',
        missing_attachments: '',
        send_email_without_the_missing_attachments_prompt: '',
    };

    constructor(
        private emailCreate: EmailCreateService,
        private remember: RememberService,
        private document: DocumentService,
        private modal: ModalService,
        private dialog: MatDialog,
        private report: ReportService,
        private file: FileService,
        private docArc: DocArcService,
        private graphAuth: GraphAuthService,
        private graphMail: GraphMailService,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService,
        @Inject(TT_STATE_DIALOG) @Optional() stateDialog?: StateDialogContext,
    ) {
        super();
        this.initFromDialog(stateDialog);

        console.log(EmailCreateComponent);
        console.log(typeof EmailCreateComponent);
    }

    public rememberEmailCreate = async () => {
        if (!!this.get) {
            try {
                await this.remember.remember('w_emailadd_get', btoa(JSON.stringify(this.get)));
                console.log('rememberEmailCreate runs');
                if (this.initialSelectedReport !== -1) await this.remember.remember('goto.emailcreate.selectedreport', this.initialSelectedReport);
            } catch (error) {
                console.error(error);
            }
        }
    };

    public searchEmailRecipients = (event: { value: string | null }) => {
        return this.emailCreate.searchEmailRecipients({ searchtext: event.value ?? '' });
    };

    public get hasDocumentTabs(): boolean {
        return this.visibleDocumentTabs.length > 0;
    }

    public get showExternalDocumentsTab(): boolean {
        return this.get?.setting_show_tab_external_documents === '1';
    }

    public get sirvImages(): EmailCreatePictureBankItem[] {
        switch (this.selectedSirvTab) {
            case 'ARGTYPE':
                return this.argtypeSirvImages;
            case 'CUSTOMER':
                return this.customerSirvImages;
            case 'PRODUCT':
                return this.productSirvImages;
            case 'CUSTACT':
                return this.custactSirvImages;
            default:
                return [];
        }
    }

    public getSirvImageUrl(fullUrl: string): string {
        return `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}w=500`;
    }

    public trackSirvImage(_index: number, sirvImage: EmailCreatePictureBankItem): string {
        return sirvImage.picturebank_keyno;
    }

    public prependSirvImage(sirvImage: EmailCreatePictureBankItem): void {
        if (!this.get) return;

        const imageUrl = this.getSirvImageUrl(sirvImage.full_url).replaceAll('&', '&amp;').replaceAll('"', '&quot;');
        this.get.email_body = `<img src="${imageUrl}" />${this.get.email_body ?? ''}`;
        this.cdr.markForCheck();
    }

    public get includedAttachments(): EmailCreateIncludedAttachment[] {
        const reports: EmailCreateIncludedAttachment[] = this.getSelectedItems(this.reports).map((report) => ({
            kind: 'report',
            trackId: `report.${report.item_id}`,
            sourceId: report.item_id,
            name: report.item_name,
            icon: this.getAttachmentListIcon(report.item_glyphicon, 'far-file-alt'),
            iconColor: report.item_glyphicon_color,
            sourceTranslation: 'bg_attatchment_report',
        }));
        const documents: EmailCreateIncludedAttachment[] = this.getSelectedDocuments().map((document) => ({
            kind: 'document',
            trackId: `document.${document.item_id}`,
            sourceId: document.item_id,
            name: document.item_name,
            icon: this.getAttachmentListIcon(document.item_glyphicon, 'far-file'),
            iconColor: document.item_glyphicon_color,
            sourceTranslation: this.getDocumentSourceTranslation(document.item_id),
        }));
        const externalFiles: EmailCreateIncludedAttachment[] = this.externalAttachments.map((file) => ({
            kind: 'external',
            trackId: file,
            name: file.name,
            icon: 'fal fa-file',
            sourceTranslation: 'bg_attatchment_external',
            file: file,
            size: file.size,
        }));

        return [...reports, ...documents, ...externalFiles];
    }

    public trackIncludedAttachment(_index: number, attachment: EmailCreateIncludedAttachment): string | File {
        return attachment.trackId;
    }

    public removeIncludedAttachment(attachment: EmailCreateIncludedAttachment): void {
        if (attachment.kind === 'report') {
            this.reports = this.reports.map((report) => (report.item_id === attachment.sourceId ? { ...report, item_is_selected: '0' } : report));
        } else if (attachment.kind === 'document') {
            this.customerDocuments = this.deselectDocuments(this.customerDocuments, attachment.sourceId);
            this.argtypeDocuments = this.deselectDocuments(this.argtypeDocuments, attachment.sourceId);
            this.productDocuments = this.deselectDocuments(this.productDocuments, attachment.sourceId);
            this.custactDocuments = this.deselectDocuments(this.custactDocuments, attachment.sourceId);
        } else if (attachment.file) {
            this.externalAttachments = this.externalAttachments.filter((file) => file !== attachment.file);
        }

        this.cdr.markForCheck();
    }

    public formatFileSize(size: number): string {
        if (size < 1024) return `${size} B`;

        const units = ['KB', 'MB', 'GB'];
        let value = size / 1024;
        let unitIndex = 0;

        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex++;
        }

        return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
    }

    public onAttachmentTabChanged(selectedTab: string): void {
        this.selectedAttachmentTab = selectedTab;
        this.cdr.detectChanges();
        window.setTimeout(() => this.cdr.detectChanges());
    }

    public get showCustomerDocumentsTab(): boolean {
        return this.get?.show_tab_cust_no_documents === '1';
    }

    public get showArgtypeDocumentsTab(): boolean {
        return this.get?.show_tab_argtype_documents === '1' && !this.isDedicatedDocumentArgtype(this.get?.argtype);
    }

    public get showProductDocumentsTab(): boolean {
        return this.get?.show_tab_prod_id_documents === '1';
    }

    public get showCustactDocumentsTab(): boolean {
        return this.get?.show_tab_custact_no_documents === '1';
    }

    public async openDocumentSettings(event: MouseEvent) {
        await this.openSettings(event, 'documents');
    }

    public async openEmailCreateSettings(event: MouseEvent) {
        await this.openSettings(event, 'emailcreate');
    }

    private get visibleDocumentTabs(): EmailCreateDocumentTabId[] {
        const tabs: EmailCreateDocumentTabId[] = [];

        if (this.showArgtypeDocumentsTab) tabs.push('ARGTYPE');
        if (this.showCustomerDocumentsTab) tabs.push('CUSTOMER');
        if (this.showProductDocumentsTab) tabs.push('PRODUCT');
        if (this.showCustactDocumentsTab) tabs.push('CUSTACT');

        return tabs;
    }

    public progressModal?: ProgressModalComponent;

    public async sendEmail() {
        if (!this.get) return;

        this.sending = true;
        const externalDocuments: EmailCreateSendExternalAttachmentItem[] = [];
        let emailAccepted = false;

        try {
            await this.uploadExternalAttachments(externalDocuments);
            const results = await this.getAllFileDetails();

            const documents = [...this.getExistingDocArcValues(results), ...externalDocuments];
            const [docArcReports, sendByClient] = await Promise.all([this.addGeneratedReportsToDocArc(results), this.getSendByClient()]);

            const emailDTO: EmailCreateSendParams = {
                recipient_email: this.get.recipient_email,
                copy_email: this.get.copy_email,
                bcc_email: this.get.bcc_email,
                from_email: this.get.from_email,
                email_heading: this.base64UrlEncodeUnicode(this.get.email_heading),
                email_body: this.base64UrlEncodeUnicode(this.get.email_body),
                webpage: this.get.webpage,
                argtype: this.get.argtype,
                argvalue: this.get.argvalue,
                reports: docArcReports,
                documents: documents,
                send_by_client: sendByClient,
                emailtemplate_keyno: this.get.emailtemplate_keyno,
            };

            const response = await this.sendEmailCreate(emailDTO);
            emailAccepted = true;

            if (sendByClient === '1' && 'mailinfo' in response && Array.isArray(response.mailinfo)) {
                for (let mail of response.mailinfo) {
                    await this.sendEmailWithOffice(mail);
                }
            }

            this.externalAttachments = [];
            this.submit.emit(this.get);
        } catch (error) {
            if (!emailAccepted) {
                await this.deleteUploadedExternalAttachments(externalDocuments);
            }

            console.error(error);
        } finally {
            this.sending = false;
            this.cdr.markForCheck();
            this.progressModal?.dialogRef.close();
            delete this.progressModal;
        }
    }

    private async getAllFileDetails() {
        const allFileDetailsPromises = this.getAllFileDetailsPromises();

        this.progressModal = this.modal.openProgressDialog({ value: 0, max: allFileDetailsPromises.length, indeterminate: true, label: this.translations.retrieving_files_from_fileserver, translateLabel: false, disableClose: true, hideSublabel: true });

        const results = await Promise.allSettled(allFileDetailsPromises);

        if (results.some((result) => result.status === 'rejected')) {
            const confirm = await this.modal.openConfirmDialog({ type: 'warning', translateTitle: false, title: this.translations.missing_attachments, translateMessage: false, message: this.translations.send_email_without_the_missing_attachments_prompt + ' \n' + (results.filter((result) => result.status === 'rejected') as PromiseRejectedResult[]).reduce((acc, result) => acc + '\n' + this.findItemNameFromPromiseReject(result), '\n' + this.translations.missing_attachments + ':') });

            if (!confirm) {
                this.progressModal.dialogRef.close();
                throw Error('Manglende vedlegg');
            }
        }

        return results;
    }

    private getAllFileDetailsPromises() {
        const reportFiles = this.getSelectedItems(this.reports).map((item) => this.getReport(item, 'report.' + item.item_id));
        const documentFiles = this.getSelectedDocuments().map((item) => this.getFileItem(item, 'document.' + item.item_id));

        return [...reportFiles, ...documentFiles];
    }

    private async addGeneratedReportsToDocArc(results: PromiseSettledResult<{ item: DocumentItem | ReportItem; file: FileItem }>[]) {
        this.progressModal?.setLabel(this.translations.saving_reports, false);
        const successfulResults = results.filter((result) => result.status === 'fulfilled') as PromiseFulfilledResult<{ item: DocumentItem | ReportItem; file: FileItem }>[];
        const reports = successfulResults.filter((result) => 'report_id' in result.value.item) as PromiseFulfilledResult<{ item: ReportItem; file: FileItem }>[];

        return Promise.all(
            reports.map(async ({ value }) => {
                const report = await this.docArc.add({
                    filename: value.file.fileName!,
                    contentType: value.file.contentType || '',
                    latitude: 0,
                    longitude: 0,
                    altitude: 0,
                    createdTimestamp: value.file.createdTimestamp || '',
                    size: value.file.size!,
                    checksum_md5: value.file.checksum!,
                    argType: this.get?.argtype,
                    argValue: this.get?.argvalue,
                    p2ReportdefKeyno: value.item.p2_reportdef_keyno,
                });

                return { ...report, is_external: '0' as const };
            }),
        );
    }

    private getExistingDocArcValues(results: PromiseSettledResult<{ item: DocumentItem | ReportItem; file: FileItem }>[]): { is_external: '0'; docarc_keyno: `${number}` | number; docfilename: string }[] {
        const successfulResults = results.filter((result) => result.status === 'fulfilled') as PromiseFulfilledResult<{ item: DocumentItem | ReportItem; file: FileItem }>[];
        const documents = successfulResults.filter((result) => 'docfilename' in result.value.item && !('report_id' in result.value.item)) as PromiseFulfilledResult<{ item: DocumentItem; file: FileItem }>[];

        return documents.map(({ value }) => ({ is_external: '0', docarc_keyno: value.item.item_id, docfilename: value.item.docfilename }));
    }

    private async sendEmailCreate(params: EmailCreateSendParams) {
        this.progressModal?.setLabel(this.translations.sending_email, false);
        const data = await this.emailCreate.sendEmail(params);

        const response = Array.isArray(data) ? data[0] : data;

        if ('' + response.errorcode !== '0') {
            this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
            throw Error(response.errormessage);
        }

        return response;
    }

    private async sendEmailWithOffice(mail: EmailCreateClientSend) {
        try {
            await this.graphMail.sendMessage({ userId: mail.from, subject: this.base64UrlDecode(mail.subject), contentType: 'html', content: this.base64UrlDecode(mail.bodyHtml), recipients: mail.to, ccRecipients: mail.cc, bccRecipients: mail.bcc, attachments: mail.attachments, saveToSentItems: true });
        } catch (error) {
            let message = '';

            if (!!error && typeof error === 'object' && 'body' in error && typeof error.body === 'string') {
                try {
                    message = JSON.parse(error.body).message;
                } catch (e) {
                    console.error(e);
                }
            } else {
                message = JSON.stringify(error, null, 2);
            }

            this.modal.show({ type: 'warning', title: 'Office 365 error', message: message });
            throw error;
        }
    }

    private async getSendByClient(): Promise<'1' | '0'> {
        try {
            await this.graphAuth.getAccount();
            return '1';
        } catch (error) {
            return '0';
        }
    }

    private findItemNameFromPromiseReject(result: PromiseRejectedResult) {
        if ('config' in result.reason && 'instanceId' in result.reason.config && typeof result.reason.config.instanceId === 'string') {
            const parts: string[] = result.reason.config.instanceId.split('.');
            const type = parts[0];

            let list: (ReportItem | DocumentItem)[] | undefined;

            if (type === 'report') {
                list = this.reports;
            } else if (type === 'document') {
                list = this.documentLists.flat();
            }

            // // @ts-ignore
            const item = list?.find((item: ReportItem | DocumentItem) => item.item_id === parts[1]);

            if (item) return item.item_name;
        }

        return result.reason?.data?.message ?? '';
    }

    private getReport = async (report: ReportItem, instanceId?: string): Promise<{ item: ReportItem; file: FileItem }> => {
        let reportFile: FileItem | null = null;

        if (+report.report_type_keyno === 1) {
            reportFile = await this.report.getTelerikReport(report.report_id, { [this.get!.argtype]: this.get!.argvalue }, instanceId, true);
        } else if (+report.report_type_keyno === 2) {
            reportFile = await this.report.getHTMLReport(+report.p2_reportdef_keyno, `{"${this.get!.argtype}": "${this.get!.argvalue}"}`, instanceId, true);
        }

        if (!!reportFile) return { item: report, file: reportFile };
        console.log('error the report was null', report);
        throw Error('Error occured');
    };

    private getFileItem = async (item: DocumentItem, instanceId?: string): Promise<{ item: DocumentItem; file: FileItem }> => {
        return { item: item, file: await this.file.getFileDetails({ fileName: item.docfilename, instanceId: instanceId, suppressErrorMessage: true }) };
    };

    private getSelectedItems<TType extends { item_is_selected: '1' | '0' }>(list: TType[]): TType[] {
        return list.filter((item) => item.item_is_selected === '1');
    }

    private get documentLists(): DocumentItem[][] {
        return [this.customerDocuments, this.argtypeDocuments, this.productDocuments, this.custactDocuments];
    }

    private getSelectedDocuments(): DocumentItem[] {
        const selectedDocuments = new Map<string, DocumentItem>();

        for (const document of this.documentLists.flat()) {
            if (document.item_is_selected === '1' && !selectedDocuments.has(document.item_id)) {
                selectedDocuments.set(document.item_id, document);
            }
        }

        return [...selectedDocuments.values()];
    }

    private getDocumentSourceTranslation(itemId: string): string {
        if (this.customerDocuments.some((document) => document.item_id === itemId && document.item_is_selected === '1')) return 'bg_attatchdocs_customer';
        if (this.argtypeDocuments.some((document) => document.item_id === itemId && document.item_is_selected === '1')) return ARGTYPE_DOCUMENTS_TAB_TRANSLATION;
        if (this.productDocuments.some((document) => document.item_id === itemId && document.item_is_selected === '1')) return 'bg_attatchdocs_products';

        return 'bg_attatchdocs_custact';
    }

    private getAttachmentListIcon(icon: string, fallback: string): string {
        const listIcon = icon || fallback;
        const separatorIndex = listIcon.indexOf('-');

        if (listIcon.startsWith('fa') && [2, 3].includes(separatorIndex)) {
            return `${listIcon.slice(0, separatorIndex)} fa${listIcon.slice(separatorIndex)}`;
        }

        return `glyphicon ${listIcon}`;
    }

    private deselectDocuments(documents: DocumentItem[], itemId?: string): DocumentItem[] {
        return documents.map((document) => (document.item_id === itemId ? { ...document, item_is_selected: '0' } : document));
    }

    private async loadEmailTemplates() {
        if (!!this.params) {
            this.emailTemplates = await this.emailCreate.getEmailTemplates(this.params);
        }
    }

    private async loadReports() {
        if (!!this.params) {
            this.reports = await this.emailCreate.getReports(this.params);

            console.log('This runs');

            for (let i = 0; i < this.reports.length; i++) {
                if (this.reports[i].item_is_selected === '1') {
                    this.initialSelectedReport = parseInt(this.reports[i].p2_reportdef_keyno);
                    break;
                }
            }
        }
    }

    private async loadCustomerDocuments() {
        if (!this.get || !this.showCustomerDocumentsTab || !(+this.get.cust_no > 0)) return;

        this.customerDocuments = await this.document.getDocuments({
            argtype: 'cust_no',
            argvalue: this.get.cust_no,
            searchtext: '',
            glyphicon_select: '0',
            doctype: 'a',
        });

        this.cdr.markForCheck();
    }

    private async loadArgtypeDocuments() {
        if (!this.get || !this.showArgtypeDocumentsTab) return;

        this.argtypeDocuments = await this.document.getDocuments({
            argtype: this.get.argtype,
            argvalue: this.get.argvalue,
            searchtext: '',
            glyphicon_select: '0',
            doctype: 'a',
        });

        this.cdr.markForCheck();
    }

    private async loadProductDocuments() {
        if (!this.get || !this.showProductDocumentsTab) return;

        this.productDocuments = await this.document.getDocuments({
            argtype: `${this.get.argtype}_products`,
            argvalue: this.get.argvalue,
            searchtext: '',
            glyphicon_select: '0',
            doctype: 'a',
        });

        this.cdr.markForCheck();
    }

    private async loadCustactDocuments() {
        if (!this.get || !this.showCustactDocumentsTab || !(+this.get.custact_no > 0)) return;

        this.custactDocuments = await this.document.getDocuments({
            argtype: `custact_no`,
            argvalue: this.get.custact_no,
            searchtext: '',
            glyphicon_select: '0',
            doctype: 'a',
        });

        this.cdr.markForCheck();
    }

    private async loadCustomerSirvImages() {
        if (!this.params || !this.get || !this.showCustomerDocumentsTab || !(+this.get.cust_no > 0)) return;

        this.customerSirvImages = await this.emailCreate.getPictureBanks({ ...this.params, argtype: 'cust_no', argvalue: this.get.cust_no });
        this.cdr.markForCheck();
    }

    private async loadArgtypeSirvImages() {
        if (!this.params || !this.get || !this.showArgtypeDocumentsTab) return;

        this.argtypeSirvImages = await this.emailCreate.getPictureBanks({ ...this.params, argtype: this.get.argtype, argvalue: this.get.argvalue });
        this.cdr.markForCheck();
    }

    private async loadProductSirvImages() {
        if (!this.params || !this.get || !this.showProductDocumentsTab) return;

        this.productSirvImages = await this.emailCreate.getPictureBanks({ ...this.params, argtype: `${this.get.argtype}_products`, argvalue: this.get.argvalue });
        this.cdr.markForCheck();
    }

    private async loadCustactSirvImages() {
        if (!this.params || !this.get || !this.showCustactDocumentsTab || !(+this.get.custact_no > 0)) return;

        this.custactSirvImages = await this.emailCreate.getPictureBanks({ ...this.params, argtype: 'custact_no', argvalue: this.get.custact_no });
        this.cdr.markForCheck();
    }

    private resetSirvImageLists() {
        this.customerSirvImages = [];
        this.argtypeSirvImages = [];
        this.productSirvImages = [];
        this.custactSirvImages = [];
    }

    private async loadSirvImages() {
        this.resetSirvImageLists();

        if (!this.showExternalDocumentsTab) {
            this.selectedSirvTab = '';

            if (this.selectedAttachmentTab === 'EXTERNAL') {
                this.selectedAttachmentTab = 'REPORT';
            }

            this.cdr.markForCheck();
            return;
        }

        this.selectFirstVisibleSirvTab();

        await Promise.all([this.loadCustomerSirvImages(), this.loadArgtypeSirvImages(), this.loadProductSirvImages(), this.loadCustactSirvImages()]);
        this.cdr.markForCheck();
    }

    private resetDocumentLists() {
        this.customerDocuments = [];
        this.argtypeDocuments = [];
        this.productDocuments = [];
        this.custactDocuments = [];
    }

    private selectFirstVisibleDocumentsTab() {
        const tabs = this.visibleDocumentTabs;

        if (tabs.length === 0) {
            this.selectedDocsTab = '';

            if (this.selectedAttachmentTab === 'FILE') {
                this.selectedAttachmentTab = 'REPORT';
            }

            return;
        }

        if (!tabs.includes(this.selectedDocsTab as EmailCreateDocumentTabId)) {
            this.selectedDocsTab = tabs[0];
        }
    }

    private async uploadExternalAttachments(documents: EmailCreateSendExternalAttachmentItem[]): Promise<void> {
        if (!this.get || !this.showExternalDocumentsTab || this.externalAttachments.length === 0) return;

        await this.file.uploadFiles({
            files: this.externalAttachments,
            preprocess: async (_fileName, file) => {
                const checksum = await this.file.hash(file);
                return { cancelUpload: false, checksum_md5: checksum.hash };
            },
            postprocess: async (data) => {
                const docfilename = data.localFileName || data.fileName || '';

                if (!docfilename) throw Error('Filename is required');

                const docarcParams = {
                    filename: data.fileName || docfilename,
                    localFileName: data.localFileName || '',
                    contentType: data.contentType || '',
                    originalTimestamp: data.originalDateTime || '',
                    size: data.size || 0,
                    checksum: data.checksum || '',
                    checksum_md5: data.checksum_md5 || '',
                    latitude: data.latitude || 0,
                    longitude: data.longitude || 0,
                    altitude: data.altitude || 0,
                    heading: data.heading || 0,
                    argType: this.get!.argtype,
                    argValue: this.get!.argvalue,
                };
                const docarc = await this.docArc.add(docarcParams);

                documents.push({
                    is_external: '1',
                    docarc_keyno: docarc.docarc_keyno,
                    docfilename: docarc.docfilename || docfilename,
                });
            },
        });
    }

    private async deleteUploadedExternalAttachments(documents: EmailCreateSendExternalAttachmentItem[]): Promise<void> {
        const docfilenames = [...new Set(documents.map((document) => document.docfilename).filter((docfilename) => !!docfilename))];
        const results = await Promise.allSettled(docfilenames.map((docfilename) => this.file.delete({ url: '', fileName: docfilename, confirmMessage: undefined })));

        for (const result of results) {
            if (result.status === 'rejected') console.error(result.reason);
        }
    }

    private selectFirstVisibleSirvTab() {
        const tabs = this.visibleDocumentTabs;

        if (tabs.length === 0) {
            this.selectedSirvTab = '';
            return;
        }

        if (!tabs.includes(this.selectedSirvTab as EmailCreateDocumentTabId)) {
            this.selectedSirvTab = tabs[0];
        }
    }

    private getDocumentSelections(): EmailCreateDocumentSelections {
        return {
            customer: this.getDocumentSelectionState(this.customerDocuments),
            argtype: this.getDocumentSelectionState(this.argtypeDocuments),
            product: this.getDocumentSelectionState(this.productDocuments),
            custact: this.getDocumentSelectionState(this.custactDocuments),
        };
    }

    private getDocumentSelectionState(list: DocumentItem[]): Map<string, '1' | '0'> {
        return new Map(list.map((item) => ['' + item.item_id, item.item_is_selected]));
    }

    private restoreDocumentSelections(selections: EmailCreateDocumentSelections) {
        this.restoreDocumentListSelections(this.customerDocuments, selections.customer);
        this.restoreDocumentListSelections(this.argtypeDocuments, selections.argtype);
        this.restoreDocumentListSelections(this.productDocuments, selections.product);
        this.restoreDocumentListSelections(this.custactDocuments, selections.custact);
    }

    private restoreDocumentListSelections(list: DocumentItem[], selections: Map<string, '1' | '0'>) {
        for (const item of list) {
            const selected = selections.get('' + item.item_id);

            if (selected !== undefined) {
                item.item_is_selected = selected;
            }
        }
    }

    private async loadDocuments(selections?: EmailCreateDocumentSelections) {
        this.resetDocumentLists();
        this.selectFirstVisibleDocumentsTab();

        await Promise.all([this.loadCustomerDocuments(), this.loadArgtypeDocuments(), this.loadProductDocuments(), this.loadCustactDocuments()]);

        if (!!selections) {
            this.restoreDocumentSelections(selections);
        }

        this.cdr.markForCheck();
    }

    private async openSettings(event: MouseEvent, webpageName: string) {
        event.preventDefault();

        await this.modal.openStateDialog({ state: 'webpagesettingmenu', params: webpageName }, { width: '90vw', height: '80vh' });
        await this.refreshAfterSettings();
    }

    private async refreshAfterSettings() {
        if (!this.get) return;

        const draftGet = this.get;
        const selections = this.getDocumentSelections();
        const response = this.normalizeGet(await this.emailCreate.getEmailCreate(draftGet));

        this.get = this.normalizeGet({
            ...draftGet,
            show_setting: response.show_setting,
            show_tab_cust_no_documents: response.show_tab_cust_no_documents,
            show_tab_prod_id_documents: response.show_tab_prod_id_documents,
            show_tab_custact_no_documents: response.show_tab_custact_no_documents,
            show_tab_argtype_documents: response.show_tab_argtype_documents,
            setting_show_tab_external_documents: response.setting_show_tab_external_documents,
            businessco_no: response.businessco_no,
            errorcode: response.errorcode,
            errormessage: response.errormessage,
            emailcreate_heading: response.emailcreate_heading,
            portal_user_keyno: response.portal_user_keyno,
            language_id: response.language_id,
            after_create_email_goto_orderheads: response.after_create_email_goto_orderheads,
            cust_no: response.cust_no,
            custact_no: response.custact_no,
            cust_name: response.cust_name,
        });
        this.selectFirstVisibleDocumentsTab();
        void this.setArgtypeDocumentsLabel();
        await Promise.all([this.loadDocuments(selections), this.loadSirvImages()]);

        this.cdr.markForCheck();
    }

    private async setArgtypeDocumentsLabel() {
        const get = this.get;
        this.argtypeDocumentsLabel = ARGTYPE_DOCUMENTS_TAB_TRANSLATION;

        if (!get?.argtype) return;

        const wordId = `${ARGTYPE_DOCUMENTS_TAB_TRANSLATION}_${get.argtype}`;
        const translation = await this.tryTranslate(wordId);

        if (this.get !== get) return;

        this.argtypeDocumentsLabel = this.hasTranslation(translation, wordId) ? wordId : ARGTYPE_DOCUMENTS_TAB_TRANSLATION;
        this.cdr.markForCheck();
    }

    private async tryTranslate(wordId: string): Promise<string | undefined> {
        return new Promise((resolve) => {
            let resolved = false;

            const timeout = window.setTimeout(() => {
                if (resolved) return;

                resolved = true;
                resolve(undefined);
            }, 1500);

            this.translate
                .translate(wordId)
                .then((translation) => {
                    if (resolved) return;

                    resolved = true;
                    window.clearTimeout(timeout);
                    resolve(typeof translation === 'string' ? translation : undefined);
                })
                .catch(() => {
                    if (resolved) return;

                    resolved = true;
                    window.clearTimeout(timeout);
                    resolve(undefined);
                });
        });
    }

    private hasTranslation(translation: string | undefined, wordId: string): translation is string {
        return !!translation && translation !== wordId;
    }

    private isDedicatedDocumentArgtype(argtype?: string): boolean {
        return argtype === 'cust_no' || argtype === 'prod_id' || argtype === 'custact_no';
    }

    private normalizeGet(get: EmailCreateGet): EmailCreateGet {
        return {
            ...get,
            customer_filtervalue: get.customer_filtervalue ?? '',
            order_filtervalue: get.order_filtervalue ?? '',
            product_filtervalue: get.product_filtervalue ?? '',
            show_tab_cust_no_documents: get.show_tab_cust_no_documents ?? '1',
            show_tab_prod_id_documents: get.show_tab_prod_id_documents ?? '1',
            show_tab_custact_no_documents: get.show_tab_custact_no_documents ?? '1',
            show_tab_argtype_documents: get.show_tab_argtype_documents ?? (get.argtype === 'order_internal_no' ? '1' : '0'),
            setting_show_tab_external_documents: get.setting_show_tab_external_documents ?? '0',
        };
    }

    public async loadEmailCreateGet(loadDocuments = true) {
        if (!!this.params) {
            const response = await this.emailCreate.getEmailCreate(this.get ?? { ...this.params, replyAll: ['true', true, '1', 1].includes(this.params.replyAll ?? '0') ? '1' : '0' });

            this.get = this.normalizeGet({ ...response, email_body: response.email_signature });
            this.selectFirstVisibleDocumentsTab();
            this.selectFirstVisibleSirvTab();
            void this.setArgtypeDocumentsLabel();

            if (loadDocuments) {
                await this.loadDocuments();
            }

            this.cdr.markForCheck();
        }
    }

    private setBeforeGet(data: EmailCreateBeforeGet) {
        const decoded = atob(data.previous_class);

        this.get = this.normalizeGet(JSON.parse(decoded));
        this.selectFirstVisibleDocumentsTab();
        this.selectFirstVisibleSirvTab();
        void this.setArgtypeDocumentsLabel();

        if (!this.get) return;

        if (data.emails_to_add_to !== '') {
            if (this.get.recipient_email !== '' && this.get.recipient_email.indexOf(';') <= 1) {
                this.get.recipient_email += '; ';
            }
            this.get.recipient_email += data.emails_to_add_to;
        }

        if (data.emails_to_add_copy !== '') {
            if (this.get.copy_email !== '' && this.get.copy_email.indexOf(';') <= 1) {
                this.get.copy_email += '; ';
            }
            this.get.copy_email += data.emails_to_add_copy;
        }

        if (data.emails_to_add_bcc !== '') {
            if (this.get.bcc_email !== '' && this.get.bcc_email.indexOf(';') <= 1) {
                this.get.bcc_email += '; ';
            }
            this.get.bcc_email += data.emails_to_add_bcc;
        }
    }

    private async loadGet() {
        const data = await this.emailCreate.getEmailCreateBefore();

        if (data.previous_class === '') {
            await this.loadEmailCreateGet(false);
        } else {
            try {
                this.setBeforeGet(data);
            } catch (error) {
                console.error(error);
            }
        }

        this.getLoaded.emit(this.get);

        if (this.get?.errorcode !== '0') {
            this.modal.show({ type: 'warning', title: 'warning', translateTitle: true, message: this.get?.errormessage });
        }
    }

    private async init() {
        this.translateWords();
        this.reportListButtons = [
            {
                id: 'preview',
                icon: 'far fa-file-search',
                type: 'primary',
                tooltip: 'preview',
                onClick: (event) => this.modal.openReportPreviewDialog({ heading: event.item.item_name, p2_reportdef_keyno: event.item.p2_reportdef_keyno, report_type_keyno: event.item.report_type_keyno, parameters: { [this.params!.argtype]: this.params!.argvalue } }),
            },
        ];

        void Promise.allSettled([this.loadEmailTemplates(), this.loadReports()]);

        try {
            await this.graphAuth.getAccount();
        } catch (error) {
            console.error(error);
        }

        await this.loadGet();
        await Promise.all([this.loadDocuments(), this.loadSirvImages()]);
    }

    private async translateWords() {
        const translations = await this.translate.translateBatch(this.translations);
        const keys = Object.keys(translations) as (keyof typeof translations)[];

        for (const key of keys) {
            this.translations[key] = translations[key];
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params'] && JSON.stringify(changes['params'].currentValue) !== JSON.stringify(changes['params'].previousValue)) {
            this.init();
        }
    }

    private async initFromDialog(stateDialog?: StateDialogContext) {
        if (!!stateDialog?.params && 'webpage' in stateDialog.params && 'argtype' in stateDialog.params && 'argvalue' in stateDialog.params) {
            this.params = stateDialog.params as unknown as EmailCreateParams;
            this.submit.subscribe({ next: () => stateDialog.close() });
            stateDialog.setSize({ width: '90vw', height: '80vh' });
            await this.init();
            stateDialog.setHeading(this.get?.emailcreate_heading ?? '');
        }
    }
}
